import React, {useCallback, useEffect, useState} from 'react';
import {Controller, Resolver, useForm} from 'react-hook-form';
import {StyleSheet, View} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {zodResolver} from '@hookform/resolvers/zod';
import {useFocusEffect} from '@react-navigation/native';
import {ThemeProvider} from '@rn-vui/themed';

import Button from '../../components/themed/Button';
import Text from '../../components/themed/Text';
import TextInput from '../../components/themed/TextInput';
import {InputLabel} from '../../components/ui-controls/form/InputLabel';
import Modal from '../../components/ui-controls/Modal';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import rewardsTheme from '../../theme/rewardsTheme';

import {PasswordRequirementsIndicator} from './components/PasswordRequirementsIndicator';
import TogglePasswordVisibleIconButton from './components/TogglePasswordVisibleIconButton';
import {WeakPasswordBypass} from './components/WeakPasswordBypass';
import {PasswordResetFormData, passwordResetSchema} from './schemas/authSchemas';
import {usePasswordInput} from './hooks';

// Theme colors and layout constants
const COLORS = {
  error: '#C81E1E',
} as const;

const BUTTON_WIDTH = {
  submit: 194,
  modal: 150,
} as const;

/**
 * Password Reset Screen
 *
 * Allows users to reset their password via email link token.
 *
 * FLOW:
 * 1. User enters new password
 * 2. Client-side Zod validation (password requirements indicator)
 * 3. Submit to backend - validates password strength during reset
 * 4. If backend returns weak password error, show bypass checkbox
 * 5. On success, show confirmation modal and redirect to home
 */
const PasswordReset = ({
  navigation,
  route,
}: RouteNavigatorScreenProps<AppRoute.ACCOUNT_PASSWORD_RESET>) => {
  const {userStore} = useStores();
  const [isPasswordUpdatedModalVisible, setIsPasswordUpdatedModalVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [isTokenMissing, setIsTokenMissing] = useState(false);

  const form = useForm<PasswordResetFormData>({
    resolver: zodResolver(passwordResetSchema) as Resolver<PasswordResetFormData>,
    defaultValues: {
      password: '',
      passwordValidationBypass: false,
    },
    mode: 'onBlur',
  });

  const {
    control,
    handleSubmit,
    formState: {isSubmitted},
    reset,
  } = form;

  const password = usePasswordInput({form});
  const {reset: resetPasswordInput} = password;

  // Validate token exists on mount
  useEffect(() => {
    if (!route.params?.token) {
      setIsTokenMissing(true);
      setApiError('Invalid password reset link. Please request a new one.');
    }
  }, [route.params?.token]);

  // Reset form state when screen loses focus
  useFocusEffect(
    useCallback(() => {
      return () => {
        reset();
        resetPasswordInput();
        setIsPasswordUpdatedModalVisible(false);
        setApiError(null);
        setIsTokenMissing(false);
      };
    }, [reset, resetPasswordInput]),
  );

  const onSubmit = async (values: PasswordResetFormData) => {
    // Don't submit if token is missing
    if (!route.params?.token) {
      setApiError('Invalid password reset link. Please request a new one.');
      return;
    }

    setIsSubmitting(true);
    setApiError(null);

    // Submit password reset - backend validates password strength
    try {
      const result = await userStore.resetPassword(
        {password: values.password, passwordValidationBypass: values.passwordValidationBypass},
        route.params.token,
      );

      if (result.success) {
        setIsPasswordUpdatedModalVisible(true);
      } else {
        // Check if backend flagged weak password (userStore sets this flag)
        if (userStore.showWeakPasswordCheckbox) {
          password.handleWeakPasswordError({
            status: 'PASSWORD_TOO_WEAK',
            message: result.message || 'Password is too weak or may be compromised',
          });
        } else {
          setApiError(result.message || 'Failed to update password.');
        }
      }
    } catch (_err) {
      setApiError('Failed to update password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onConfirmationModalContinue = () => {
    setIsPasswordUpdatedModalVisible(false);
    navigation.navigate(AppRoute.HOME);
  };

  return (
    <ThemeProvider theme={rewardsTheme}>
      <KeyboardAwareScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollViewContent}
        keyboardShouldPersistTaps="always"
        enableOnAndroid
        extraScrollHeight={40}>
        <View style={styles.formContainer}>
          {/* Token Missing Error */}
          {isTokenMissing ? (
            <View style={styles.errorContainer}>
              <Text style={styles.errorText}>{apiError}</Text>
              <Button
                title="Go to Home"
                onPress={() => navigation.navigate(AppRoute.HOME)}
                buttonStyle={styles.errorButton}
              />
            </View>
          ) : (
            <>
              {/* Password Field */}
              <Controller
                control={control}
                name="password"
                render={({field: {onChange, onBlur, value}, fieldState: {isTouched}}) => (
                  <>
                    <TextInput
                      autoCapitalize="none"
                      label={<InputLabel required>New password</InputLabel>}
                      onBlur={() => {
                        password.passwordFieldHandlers.onBlur();
                        onBlur();
                      }}
                      onFocus={password.passwordFieldHandlers.onFocus}
                      onChangeText={(text: string) => {
                        password.passwordFieldHandlers.onChangeText(text);
                        onChange(text);
                        if (apiError) {
                          setApiError(null);
                        }
                      }}
                      secureTextEntry={!password.isPasswordVisible}
                      value={value || ''}
                      touched={isTouched || isSubmitted}
                      errorMessage={undefined}
                      errorMessageWhenTouched={undefined}
                      renderErrorMessage={false}
                      rightIconContainerStyle={styles.rightIconContainer}
                      rightIcon={
                        <TogglePasswordVisibleIconButton {...password.toggleButtonProps} />
                      }
                    />
                    <PasswordRequirementsIndicator {...password.indicatorProps} />
                  </>
                )}
              />

              {/* API Error Message */}
              {!!apiError && <Text style={styles.apiErrorText}>{apiError}</Text>}

              {/* Weak Password Bypass Checkbox */}
              <WeakPasswordBypass {...password.bypassCheckboxProps} />

              {/* Submit Button */}
              <View style={styles.submitButtonContainer}>
                <Button
                  title="Change password"
                  onPress={() => handleSubmit(onSubmit)()}
                  disabled={isSubmitting}
                  loading={isSubmitting}
                  buttonStyle={styles.submitButton}
                />
              </View>
            </>
          )}
        </View>
        <Modal
          isVisible={isPasswordUpdatedModalVisible}
          title="Password updated. Please log in with your new password.">
          <Button
            title="Continue"
            onPress={onConfirmationModalContinue}
            buttonStyle={styles.modalButton}
          />
        </Modal>
      </KeyboardAwareScrollView>
    </ThemeProvider>
  );
};

export default PasswordReset;

const styles = StyleSheet.create({
  scrollView: {
    backgroundColor: 'white',
    flex: 1,
  },
  scrollViewContent: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingHorizontal: 16,
  },
  formContainer: {
    gap: 8,
    width: '100%',
    maxWidth: 500,
    alignSelf: 'center',
  },
  rightIconContainer: {
    position: 'absolute',
    right: 10,
  },
  apiErrorText: {
    color: COLORS.error,
    fontSize: 14,
    fontWeight: '600',
    marginTop: 8,
  },
  submitButtonContainer: {
    alignItems: 'center',
    marginTop: 16,
  },
  submitButton: {
    width: BUTTON_WIDTH.submit,
  },
  modalButton: {
    width: BUTTON_WIDTH.modal,
    marginTop: 16,
  },
  errorContainer: {
    alignItems: 'center',
    gap: 16,
  },
  errorText: {
    color: COLORS.error,
    fontSize: 16,
    textAlign: 'center',
  },
  errorButton: {
    marginTop: 8,
  },
});
